import {
    createSelector,
    createEntityAdapter,
} from '@reduxjs/toolkit'
import { apiSlice } from '../../app/api/apiSlice'

const userReflectionsAdapter = createEntityAdapter({})

const initialState = userReflectionsAdapter.getInitialState()

export const userReflectionsApiSlice = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getUserReflections: builder.query({
            query: () => ({
                url: '/userReflections/getUserReflections',
                validateStatus: (response, result) => {
                    return response.status === 200 && !result.isError
                }
            }),
            transformResponse: responseData => {
                const loadedUserReflections = responseData.map(userReflection => {
                    userReflection.id = userReflection._id
                    return userReflection
                })
                return userReflectionsAdapter.setAll(initialState, loadedUserReflections)
            },
            providesTags: (result, error, arg) => {
                if(result?.ids) {
                    return [
                        {type: 'UserReflection', id: 'LIST'},
                        ...result.ids.map(id => ({ type: 'UserReflection', id}))
                    ]
                } else {
                    return [ { type: 'UserReflection', id: 'LIST'}]
                }
            },
        }),
        createNewUserReflection: builder.mutation({
            query: initialUserReflection => ({
                url: '/userReflections/createNewUserReflection',
                method: 'POST',
                body: {...initialUserReflection,}
            }),
            invalidatesTags: [
                { type: 'UserReflection', id: "LIST" }
            ] 
        }),
        updateUserReflection: builder.mutation({
            query: initialUserReflection => ({
                url: '/userReflections/updateUserReflection',
                method: 'PATCH',
                body: {...initialUserReflection,}
            }),
            invalidatesTags: (result, error, arg) => [
                {type: 'UserReflection', id: arg.id },
                { type: 'UserReflection', id: 'LIST' }
            ],
        }),
        deleteUserReflection: builder.mutation({
            query: ({id}) => ({
                url: '/userReflections/deleteUserReflection',
                method: 'DELETE',
                body: {id}
            }),
            invalidatesTags: (result, error, arg) => [
                { type: 'UserReflection', id: arg.id}
            ]
        }),
        // 03 Dec 2025 - Get all atomic reflections for Reflection Center
        getUserReflectionsAll: builder.query({
            query: () => ({
                url: '/userReflections/getUserReflectionsAll',
                validateStatus: (response, result) =>
                    response.status === 200 && !result?.isError,
            }),
            // We do NOT normalize. Return raw array.
            // 26 Jan 2026 - Refined to handle new payload coming from backend
            transformResponse: (responseData) => {
                // 🟢 Sponsor path → backend sends an object
                if (responseData?.reflections) {
                    return {
                        reflections: responseData.reflections.map((ref) => ({
                            ...ref,
                            id: ref._id,
                        })),
                        orgDailyTotals: responseData.orgDailyTotals || [],
                        orgUserDailyTotals: responseData.orgUserDailyTotals || [], // 29 Jan 2026
                    };
                }

                // 🟢 Non-sponsor / legacy path → backend sends array
                if (Array.isArray(responseData)) {
                    return responseData.map((ref) => ({
                        ...ref,
                        id: ref._id,
                    }));
                }

                // 🟢 Fallback safety
                return [];
            },
            providesTags: (result) => {
                const reflections = Array.isArray(result)
                    ? result
                    : result?.reflections || [];

                return [
                    { type: 'UserReflectionAll', id: 'LIST' },
                    ...reflections.map((ref) => ({
                        type: 'UserReflectionAll',
                        id: ref._id,
                    })),
                ];
            },


            
            // Prior Code - DO NOT DELETE - KEPT FOR REFERENCE
            // transformResponse: (responseData) => {
            //     return responseData?.map((ref) => ({
            //         ...ref,
            //         id: ref._id, // for React key consistency
            //     })) || [];
            // },
            
            // providesTags: (result) =>
            //     result
            //         ? [
            //             { type: 'UserReflectionAll', id: 'LIST' },
            //             ...result.map((ref) => ({
            //                 type: 'UserReflectionAll',
            //                 id: ref._id,
            //             })),
            //         ]
            //         : [{ type: 'UserReflectionAll', id: 'LIST' }],
        }),

    }),
})

export const {
    useGetUserReflectionsQuery,
    useCreateNewUserReflectionMutation,
    useUpdateUserReflectionMutation,
    useDeleteUserReflectionMutation,
    // 03 Dec 2025
    useGetUserReflectionsAllQuery,
} = userReflectionsApiSlice

// returns the query result object
export const selectUserReflectionResult = userReflectionsApiSlice.endpoints.getUserReflections.select()

// create memoized selector
const selectUserReflectionsData = createSelector(
    selectUserReflectionResult,
    userReflectionResult => userReflectionResult.data // normalized state object with ids & entities
)

//getSelectors creates these selectors and we rename them with aliases using destructuring
export const {
    selectAll: selectAllUserReflections,
    selectById: selectUserReflectionById,
    selectIds: selectUserReflectionIds,
} = userReflectionsAdapter.getSelectors(state => selectUserReflectionsData(state) ?? initialState)